package ui.translation


import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.focus.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.text.input.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import compose.assets.*
import compose.assets.svg.*
import kotlinx.coroutines.*
import provider.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.widget.*


@ComposableNavNode
@Composable
fun TranslationListingUI() {
    with(TranslationListingScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun TranslationListingScope.Content(nnvl: TranslationListingScope.Wrapper) {

    val searchFiledValue = remember {
        mutableStateOf(TextFieldValue(""))
    }
    val searchFiledKey = remember {
        mutableStateOf(TextFieldValue(""))
    }

    val display = LocalDisplayProvider.current
    Box(Modifier.fillMaxSize()) {
        Column(Modifier.fillMaxSize().padding(top = 8.dp).padding(horizontal = 8.dp)) {
            if (display.isMobile) {
                HomeSearchUI(searchFiledValue, label = Res.string.translation_listing__search)
                Space(4.dp)
                SearchUI(searchFiledKey, Modifier.fillMaxWidth(), label = Res.string.translation_listing__search_key)
                Space(4.dp)
                Text(
                    text = "Translated ${nnvl.state.count { it.allTranslated }} of ${nnvl.state.count()}, left ${nnvl.state.count() - nnvl.state.count { it.allTranslated }}",
                    style = MaterialTheme.typography.labelSmall
                )
            } else {
                Row {
                    SearchUI(searchFiledValue, label = Res.string.translation_listing__search)
                    Space(16.dp)
                    SearchUI(searchFiledKey, label = Res.string.translation_listing__search_key)
                    Space(16.dp)
                    Text(
                        text = "Translated ${nnvl.state.count { it.allTranslated }} of ${nnvl.state.count()}, left ${nnvl.state.count() - nnvl.state.count { it.allTranslated }}",
                        style = MaterialTheme.typography.titleLarge,
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )

                }
            }
            Space(8.dp)
            Header(nnvl.headers)
            Box(Modifier.fillMaxSize()) {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    contentPadding = PaddingValues(vertical = 8.dp)
                ) {

                    items(SearchWrapper.use(
                        fields = { it.allValues },
                        items = SearchWrapper.use(
                            fields = { listOf(it.data.name) },
                            items = nnvl.state,
                            query = searchFiledKey,
                            sort = compareBy { 0 }
                        ),
                        query = searchFiledValue,
                        sort = compareBy<TranslationListingScope.Wrapper.Item> {
                            it.allTranslated
                        }.thenBy {
                            it.data.name
                        }
                    )) {
                        ListingItem(it)
                    }
                }
            }
        }
        if (vm.canAdd) {
            FloatingActionButton(
                onClick = { action(TranslationListingVM.Action.New) },
                modifier = Modifier.align(Alignment.BottomEnd)
                    .padding(24.dp)
            ) {
                Icon(Svg.Plus, null)
            }
        }

        if (vm.canEdit && nnvl.changed) {
            FloatingActionButton(
                onClick = { action(TranslationListingVM.Action.Save) },
                modifier = Modifier.align(Alignment.TopEnd)
                    .padding(4.dp)
            ) {
                Icon(Svg.Content_save, null)
            }
        }
    }
}

@Composable
private fun Header(headers: List<String>) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        headers.forEach { item ->
            Card(
                colors = CardDefaults.cardColors(MaterialTheme.colorScheme.secondaryContainer),
                modifier = Modifier.weight(1F)
            ) {
                Text(
                    text = item.uppercase(),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                        .padding(8.dp)
                )
            }
        }
    }
}

@Composable
private fun TranslationListingScope.ListingItem(item: TranslationListingScope.Wrapper.Item) {
    val editorDialog = DialogState.remember()

    ItemEditorDialog(editorDialog, item)

    Card(
        onClick = { editorDialog.show() },
        enabled = vm.canEdit,
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            if (item.allTranslated) Color.Unspecified
            else MaterialTheme.colorScheme.tertiaryContainer
        )
    ) {
        Column(
            modifier = Modifier.fillMaxWidth()
                .padding(8.dp)
        ) {
            Text(item.data.name)
            Space(2.dp)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                item.variants.state.forEach {
                    Card(
                        modifier = Modifier.weight(1F)
                            .fillMaxHeight(),
                        colors = CardDefaults.cardColors(
                            containerColor = MaterialTheme.colorScheme.surfaceContainer
                        ),
                    ) {
                        Text(
                            text = it.value.state.trim().trimIndent(),
                            modifier = Modifier.fillMaxWidth()
                                .padding(8.dp)
                        )
                    }
                }
            }
        }
    }
}


@Composable
private fun TranslationListingScope.ItemEditorDialog(state: DialogState, origin: TranslationListingScope.Wrapper.Item) {
    Dialog(state) {
        val item = remember {
            TranslationListingScope.Wrapper.Item(
                data = origin.data.copy(
                    variants = origin.data.variants.values.associateBy({ it.lang }, { it })
                )
            )
        }
        val focus = remember {
            FocusRequester()
        }
        DialogUI(
            onDismissRequest = { state.hide() },
            modifier = Modifier.width(512.dp).align(Alignment.Center),
            confirmButton = {
                TextButton(
                    onClick = {
                        origin.setNewValues(item.data)
                        state.hide()
                    }
                ) { Text(Res.string.dialog__action_save.rememberString()) }
            },
            dismissButton = {
                TextButton(
                    onClick = { state.hide() }
                ) { Text(Res.string.dialog__action_cancel.rememberString()) }
            },

            title = { Text(Res.string.translation_listing__editor_title.rememberString()) }
        ) {

            val stateVertical = rememberScrollState(0)
            Box(
                modifier = Modifier.fillMaxWidth()
                    .height(600.dp)
            ) {
                Box(
                    Modifier.fillMaxSize()
                        .verticalScroll(stateVertical)
                        .padding(end = 18.dp)
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        item.variants.state.forEach { variant ->
                            TextField(
                                value = variant.value.text,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .focusRequester(focus),
                                minLines = 1,
                                label = { Text(variant.data.lang.uppercase()) },
                                onValueChange = { variant.value.set(it) })
                        }
                    }
                }
                VerticalScrollbar(
                    modifier = Modifier
                        .align(Alignment.CenterEnd)
                        .fillMaxHeight(),
                    adapter = rememberScrollbarAdapter(stateVertical)
                )
            }
        }
        LaunchedEffect(Unit) {
            delay(100)
            focus.requestFocus()
        }
    }
}


